import { useNavigate } from 'react-router-dom'
import './GameRoomHeader.css'

export interface Player {
  name: string
  avatar?: string
  status?: 'active' | 'thinking' | 'disconnected'
}

export interface GameRoomHeaderProps {
  gameTitle: string
  gameIcon?: string
  mode: 'ai' | 'network' | 'local'
  players: Player[]
  sessionCode?: string
  onBack?: () => void
  compact?: boolean
}

export function GameRoomHeader({
  gameTitle,
  gameIcon,
  mode,
  players,
  sessionCode,
  onBack,
  compact = false
}: GameRoomHeaderProps) {
  const navigate = useNavigate()

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      navigate('/games')
    }
  }

  const handleCopyCode = async () => {
    if (!sessionCode) return

    try {
      await navigator.clipboard.writeText(sessionCode)
      // TODO: Show success notification
    } catch (err) {
      console.error('Failed to copy session code:', err)
    }
  }

  const getStatusColor = (status?: Player['status']) => {
    switch (status) {
      case 'active': return 'var(--success)'
      case 'thinking': return 'var(--warning)'
      case 'disconnected': return 'var(--error)'
      default: return 'var(--text-secondary)'
    }
  }

  const getModeLabel = () => {
    switch (mode) {
      case 'ai': return 'vs AI'
      case 'network': return 'Online'
      case 'local': return 'Local'
    }
  }

  return (
    <header className={`game-room-header ${compact ? 'compact' : ''}`}>
      <div className="header-left">
        <button
          className="back-button"
          onClick={handleBack}
          aria-label="Back to games"
        >
          ←
        </button>

        <div className="game-info">
          {gameIcon && <span className="game-icon">{gameIcon}</span>}
          <h1 className="game-title">{gameTitle}</h1>
          <span className="mode-badge">{getModeLabel()}</span>
        </div>
      </div>

      <div className="header-center">
        <div className="players-list">
          {players.map((player, index) => (
            <div key={index} className="player-item">
              <div className="player-avatar-wrapper">
                {player.avatar ? (
                  <img
                    src={player.avatar}
                    alt={player.name}
                    className="player-avatar"
                  />
                ) : (
                  <div className="player-avatar-placeholder">
                    {player.name.charAt(0).toUpperCase()}
                  </div>
                )}
                <div
                  className="status-indicator"
                  style={{ backgroundColor: getStatusColor(player.status) }}
                  aria-label={`${player.name} is ${player.status || 'active'}`}
                />
              </div>
              {!compact && (
                <span className="player-name">{player.name}</span>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="header-right">
        {/* Mobile-only compact avatar strip (center section hidden on mobile) */}
        {players.length > 0 && (
          <div className="header-avatars">
            {players.slice(0, 3).map((player, index) => (
              <div key={index} className="header-avatar-chip">
                {player.avatar ? (
                  <img src={player.avatar} alt={player.name} />
                ) : (
                  <div className="header-avatar-chip-placeholder">
                    {player.name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {sessionCode && mode === 'network' && (
          <button
            className="session-code-button"
            onClick={handleCopyCode}
            title="Click to copy session code"
          >
            <span className="code-label">Code:</span>
            <span className="code-value">{sessionCode}</span>
            <span className="copy-icon">📋</span>
          </button>
        )}
      </div>
    </header>
  )
}
